import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useFetchEmployeeBalances } from "src/Pages/Company/Absences/api/employee.queries";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import transformBalances from "src/utils/Company/Absences/transformBalances";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { Loader } from "src/Components/Loader/Loader";
import AbsenceTypeList from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsBalances/AbsenceTypeList";

const AbsenceRecordsBalances: FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const logStatus = useLogStatus();
  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRecordsBalances.tsx",
    });
  }

  const {
    data: balancesData,
    isFetching: isFetchingBalances,
    isError: isErrorBalances,
  } = useFetchEmployeeBalances(employeeId);

  const { active: activeBalances, inactive: inactiveBalances } = useMemo(() => {
    if (!balancesData)
      return {
        active: [],
        inactive: [],
      };

    return transformBalances(balancesData);
  }, [balancesData]);

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTab: "active" | "inactive"
  ) => {
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  const hasActiveContent = !isEmpty(activeBalances);

  const hasInactiveContent = !isEmpty(inactiveBalances);

  return (
    <>
      {isErrorBalances ? (
        <ErrorDataFetching />
      ) : (
        <Box
          sx={{ position: "relative" }}
          data-testid="absence-records-balances"
        >
          {isFetchingBalances ? (
            <Loader />
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" mb="1rem">
                <Typography variant="h6" component="h2">
                  {__("Wymiary nieobecności i obecności")}
                </Typography>
                <ToggleButtonGroup
                  value={activeTab}
                  exclusive
                  onChange={handleTabChange}
                  aria-label={__("Wybierz typ wymiaru")}
                  data-testid="absence-records-balances__toggle"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton
                    value="active"
                    aria-label={__("Wymiary aktywne")}
                    data-testid="absence-records-balances__toggle-active"
                  >
                    {__("Aktywne")}
                  </ToggleButton>
                  <ToggleButton
                    value="inactive"
                    aria-label={__("Wymiary nieaktywne")}
                    data-testid="absence-records-balances__toggle-inactive"
                  >
                    {__("Nieaktywne")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box>
                {/* Active Content */}
                <Box
                  sx={{ display: activeTab === "active" ? "block" : "none" }}
                  data-testid="absence-records-balances__active"
                >
                  {hasActiveContent ? (
                    <>
                      <AbsenceTypeList
                        employeeId={employeeId}
                        data={activeBalances}
                      />
                    </>
                  ) : (
                    <Alert
                      severity="info"
                      data-testid="absence-records-balances__empty"
                    >
                      {__("Brak wniosków do wyświetlenia.")}
                    </Alert>
                  )}
                </Box>

                {/* Inactive Content */}
                <Box
                  sx={{ display: activeTab === "inactive" ? "block" : "none" }}
                  data-testid="absence-records-balances__inactive"
                >
                  {hasInactiveContent ? (
                    <>
                      <AbsenceTypeList
                        employeeId={employeeId}
                        data={inactiveBalances}
                      />
                    </>
                  ) : (
                    <Alert
                      severity="info"
                      data-testid="absence-records-balances__empty"
                    >
                      {__("Brak wniosków do wyświetlenia.")}
                    </Alert>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default AbsenceRecordsBalances;
